import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const TYPES = [
  { key:'work',         label:'근무',  color:'#2d5a3d', bg:'#e8f2eb', icon:'✓' },
  { key:'overtime',     label:'잔업',  color:'#1e4a8c', bg:'#e8eef8', icon:'⏰' },
  { key:'special_work', label:'특근',  color:'#c0601a', bg:'#fff3e0', icon:'⭐' },
  { key:'leave',        label:'연차',  color:'#2d5a3d', bg:'#e8f2eb', icon:'🌿' },
  { key:'hourly_leave', label:'시간차', color:'#1565c0', bg:'#e3f2fd', icon:'🕐' },
  { key:'absent',       label:'결근',  color:'#b83030', bg:'#fdeaea', icon:'❌' },
]

/** DB에 leave/absent 컬럼이 없을 수 있어 memo에만 저장 (FA_JSON:...) */
const MEMO_PREFIX = 'FA_JSON:'

function parseForeignMemo(rec) {
  const memo = rec?.memo
  if (memo && typeof memo === 'string' && memo.startsWith(MEMO_PREFIX)) {
    try {
      const j = JSON.parse(memo.slice(MEMO_PREFIX.length))
      return {
        leave: !!j.leave,
        absent: !!j.absent,
        hourly_leave: Math.max(0, Number(j.hourly_leave) || 0),
        note: typeof j.note === 'string' ? j.note : ''
      }
    } catch {
      /* fallthrough */
    }
  }
  if (rec && rec.worked === false) {
    return { leave: false, absent: true, hourly_leave: 0, note: typeof memo === 'string' ? memo : '' }
  }
  return { leave: false, absent: false, hourly_leave: 0, note: typeof memo === 'string' ? memo : '' }
}

function buildForeignMemo({ leave, absent, hourly_leave, note }) {
  const hl = Math.max(0, Number(hourly_leave) || 0)
  return MEMO_PREFIX + JSON.stringify({
    leave: !!leave,
    absent: !!absent,
    hourly_leave: hl,
    note: note ?? ''
  })
}

export default function Foreign({ profile }) {
  const [workers, setWorkers] = useState([])
  const [attendance, setAttendance] = useState([]) // 전체 직원 이번달 기록
  const [yearMonth, setYearMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`
  })
  const [showWorkerModal, setShowWorkerModal] = useState(false)
  const [showCellModal, setShowCellModal] = useState(null) // { worker, date }
  const [showQuotaModal, setShowQuotaModal] = useState(false)
  const [workerForm, setWorkerForm] = useState({ name:'', nationality:'', memo:'' })
  const [cellForm, setCellForm] = useState({ worked:true, overtime:0, special_work:false, leave:false, absent:false, hourlyLeave:0, memo:'' })
  const [loading, setLoading] = useState(false)

  useEffect(() => { fetchWorkers() }, [])
  useEffect(() => { if(workers.length) fetchAttendance() }, [workers, yearMonth])

  async function fetchWorkers() {
    const { data } = await supabase.from('foreign_workers').select('*').order('created_at')
    if(data) setWorkers(data)
  }

  async function fetchAttendance() {
    const [y, m] = yearMonth.split('-').map(Number)
    const lastDay = new Date(y, m, 0).getDate() // 해당 월의 마지막 날
    const start = yearMonth + '-01'
    const end = `${yearMonth}-${String(lastDay).padStart(2,'0')}`
    const { data, error } = await supabase.from('foreign_attendance')
      .select('*').gte('work_date', start).lte('work_date', end)
    if (error) console.error('foreign_attendance 조회:', error.message)
    if (data) setAttendance(data)
  }

  function getRecord(workerId, date) {
    return attendance.find(a => a.worker_id === workerId && a.work_date === date)
  }

  function getDays() {
    const [y, m] = yearMonth.split('-').map(Number)
    const total = new Date(y, m, 0).getDate()
    return Array.from({ length: total }, (_, i) => {
      const d = String(i+1).padStart(2,'0')
      const dateStr = `${yearMonth}-${d}`
      const dow = new Date(dateStr).getDay()
      return { date: dateStr, day: i+1, dow }
    })
  }

  function openCellModal(worker, date) {
    const rec = getRecord(worker.id, date)
    const p = parseForeignMemo(rec)
    const worked = rec
      ? !p.absent && !p.leave && (rec.worked !== false || Number(rec.overtime) > 0 || !!rec.special_work || (p.hourly_leave || 0) > 0)
      : true
    setCellForm({
      worked,
      overtime: rec?.overtime || 0,
      special_work: rec?.special_work || false,
      leave: p.leave,
      absent: p.absent,
      hourlyLeave: p.hourly_leave || 0,
      memo: p.note
    })
    setShowCellModal({ worker, date })
  }

  async function saveCellForm() {
    if(!showCellModal) return
    setLoading(true)
    const workerId = showCellModal.worker.id
    const workDate = showCellModal.date
    const overtimeNum = Number(cellForm.overtime)
    const ot = Number.isFinite(overtimeNum) ? overtimeNum : 0
    const hlRaw = Number(cellForm.hourlyLeave)
    const hl = Number.isFinite(hlRaw) ? Math.max(0, hlRaw) : 0
    const worked = !cellForm.leave && !cellForm.absent && (
      !!cellForm.worked || ot > 0 || !!cellForm.special_work || hl > 0
    )
    const payload = {
      worker_id: workerId,
      work_date: workDate,
      worked,
      overtime: ot,
      special_work: !!cellForm.special_work,
      memo: buildForeignMemo({
        leave: !!cellForm.leave,
        absent: !!cellForm.absent,
        hourly_leave: cellForm.leave || cellForm.absent ? 0 : hl,
        note: cellForm.memo ?? ''
      })
    }

    const { error: err } = await putAttendanceRow(payload)
    if (err) {
      alert(err.message || '저장 실패')
      setLoading(false)
      return
    }

    setShowCellModal(null)
    await fetchAttendance()
    setLoading(false)
  }

  /** UNIQUE 없어도 동작하도록 단일 행 저장 (saveCellForm과 동일 규칙) */
  async function putAttendanceRow(payload) {
    const { data: existingRows, error: selErr } = await supabase
      .from('foreign_attendance')
      .select('id')
      .eq('worker_id', payload.worker_id)
      .eq('work_date', payload.work_date)
      .limit(1)
    if (selErr) return { error: selErr }
    const existingId = existingRows?.[0]?.id
    if (existingId) {
      return supabase.from('foreign_attendance').update(payload).eq('id', existingId)
    }
    return supabase.from('foreign_attendance').insert(payload)
  }

  function plainWorkPayload(workerId, date) {
    return {
      worker_id: workerId,
      work_date: date,
      worked: true,
      overtime: 0,
      special_work: false,
      memo: buildForeignMemo({ leave: false, absent: false, hourly_leave: 0, note: '' })
    }
  }

  async function bulkWorkOneDay(date) {
    if (!workers.length) return
    const targets = workers.filter(w => !getRecord(w.id, date))
    if (!targets.length) {
      alert('이미 모든 직원이 입력되어 있습니다.\n변경하려면 셀을 직접 눌러 수정하세요.')
      return
    }
    if (!window.confirm(`${date} — 미입력 직원 ${targets.length}명을 모두 근무 처리할까요?\n(이미 결근·연차 등이 입력된 셀은 그대로 둡니다)`)) return
    setLoading(true)
    for (const w of targets) {
      const { error } = await putAttendanceRow(plainWorkPayload(w.id, date))
      if (error) {
        alert(error.message || '일괄 근무 저장 실패')
        setLoading(false)
        return
      }
    }
    await fetchAttendance()
    setLoading(false)
  }

  async function bulkWorkOne(workerId) {
    if (!window.confirm('이 직원의 이번달 평일을 모두 근무 처리할까요?')) return
    setLoading(true)
    const weekdays = getDays().filter(d => d.dow !== 0 && d.dow !== 6)
    for (const d of weekdays) {
      const { error } = await putAttendanceRow(plainWorkPayload(workerId, d.date))
      if (error) {
        alert(error.message || '일괄 근무 저장 실패')
        setLoading(false)
        return
      }
    }
    await fetchAttendance()
    setLoading(false)
  }

  async function addWorker() {
    if(!workerForm.name) { alert('이름을 입력하세요'); return }
    setLoading(true)
    await supabase.from('foreign_workers').insert({
      name: workerForm.name, nationality: workerForm.nationality,
      memo: workerForm.memo, leave_quota: 15, leave_used: 0
    })
    setShowWorkerModal(false)
    setWorkerForm({ name:'', nationality:'', memo:'' })
    fetchWorkers()
    setLoading(false)
  }

  async function deleteWorker(id, name) {
    if(!window.confirm(`${name} 직원을 삭제할까요?`)) return
    await supabase.from('foreign_workers').delete().eq('id', id)
    fetchWorkers()
  }

  async function saveQuota(workerId, quota, used) {
    await supabase.from('foreign_workers').update({
      leave_quota: Number(quota), leave_used: Number(used)
    }).eq('id', workerId)
    fetchWorkers()
  }

  // 월 통계 (직원별)
  function getWorkerStats(workerId) {
    const recs = attendance.filter(a => a.worker_id === workerId)
    const absentDays = recs.filter(a => parseForeignMemo(a).absent).length
    const leaveDays = recs.filter(a => parseForeignMemo(a).leave).length
    const workedDays = recs.filter(a => {
      const p = parseForeignMemo(a)
      return !p.absent && !p.leave && a.worked !== false
    }).length
    const overtime = recs.reduce((s,a) => s + (a.overtime||0), 0)
    const special = recs.filter(a => a.special_work).length
    const hourlyLeaveHours = recs.reduce((s, a) => s + (parseForeignMemo(a).hourly_leave || 0), 0)
    return { workedDays, absentDays, leaveDays, overtime, special, hourlyLeaveHours }
  }

  const days = getDays()

  /** 셀 하나를 CSV용 텍스트로 변환 */
  function cellText(rec) {
    if (!rec) return ''
    const p = parseForeignMemo(rec)
    if (p.absent) return '결근'
    if (p.leave) return '연차'
    const parts = []
    if (rec.worked !== false) parts.push('근무')
    if ((p.hourly_leave || 0) > 0) parts.push(`시간차${p.hourly_leave}h`)
    if (rec.special_work) parts.push('특근')
    if (rec.overtime > 0) parts.push(`잔업${rec.overtime}h`)
    return parts.join('/')
  }

  function exportCsv() {
    if (!workers.length) { alert('내보낼 직원이 없습니다.'); return }
    const esc = v => {
      const s = String(v ?? '')
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const dayHeaders = days.map(d => `${d.day}(${'일월화수목금토'[d.dow]})`)
    const header = ['직원', '국적', ...dayHeaders, '근무일', '잔업(h)', '특근', '연차', '시간차(h)', '결근', '총연차', '사용연차', '잔여연차']
    const rows = workers.map(w => {
      const stats = getWorkerStats(w.id)
      const quota = w.leave_quota ?? 15
      const used = w.leave_used ?? 0
      return [
        w.name, w.nationality || '',
        ...days.map(d => cellText(getRecord(w.id, d.date))),
        stats.workedDays, stats.overtime, stats.special, stats.leaveDays,
        stats.hourlyLeaveHours, stats.absentDays, quota, used, quota - used,
      ]
    })
    const csv = [
      [`${yearMonth} 외국인 근태 현황`],
      [],
      header,
      ...rows,
    ].map(r => r.map(esc).join(',')).join('\r\n')

    // BOM(U+FEFF): 엑셀이 UTF-8 한글을 깨지 않고 열도록 함
    const bom = String.fromCharCode(0xFEFF)
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `외국인근태_${yearMonth}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function cellBg(rec, dow) {
    if(!rec) return dow===0||dow===6 ? '#f9f8f6' : '#fff'
    const p = parseForeignMemo(rec)
    if(p.absent) return '#fdeaea'
    if(p.leave) return '#e8f2eb'
    if((p.hourly_leave || 0) > 0) return '#e3f2fd'
    if(rec.special_work) return '#fff3e0'
    if(rec.overtime > 0) return '#e8eef8'
    return dow===0||dow===6 ? '#f9f8f6' : '#fff'
  }

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>외국인 근태 관리</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>월별 전체 현황 · 근무·잔업·특근·연차·시간차·결근</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)}
            style={{ padding:'8px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit' }} />
          <button type="button" onClick={exportCsv}
            style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e5e3de', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            📥 엑셀
          </button>
          <button type="button" onClick={() => setShowQuotaModal(true)}
            style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e5e3de', background:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            ✏️ 연차
          </button>
          <button type="button" onClick={() => setShowWorkerModal(true)}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            + 직원 등록
          </button>
        </div>
      </div>

      {/* 범례 */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <span style={{ fontSize:11, color:'#6b6860' }}>범례:</span>
        {TYPES.map(t => (
          <span key={t.key} style={{ fontSize:11, background:t.bg, color:t.color, padding:'3px 8px', borderRadius:5, fontWeight:500 }}>
            {t.icon} {t.label}
          </span>
        ))}
        <span style={{ fontSize:11, color:'#a09e99' }}>· 날짜 머리글 클릭 = 그날 미입력 직원 전체 근무 처리</span>
      </div>

      {workers.length === 0 ? (
        <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:60, textAlign:'center', color:'#a09e99' }}>
          <div style={{ fontSize:32, marginBottom:10 }}>👷</div>
          <div style={{ fontSize:13 }}>+ 직원 등록 버튼으로 직원을 추가하세요</div>
        </div>
      ) : (
        <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflow:'auto' }}>
          <table style={{ borderCollapse:'collapse', minWidth:'100%' }}>
            <thead>
              <tr style={{ background:'#f9f8f6' }}>
                {/* 직원명 헤더 */}
                <th style={{ padding:'10px 14px', fontSize:12, fontWeight:700, color:'#1a1916', borderBottom:'1px solid #e5e3de', borderRight:'1px solid #e5e3de', textAlign:'left', minWidth:100, position:'sticky', left:0, background:'#f9f8f6', zIndex:10 }}>
                  직원
                </th>
                {/* 날짜 헤더 — 클릭 시 해당일 전체 직원 근무 처리 */}
                {days.map(({ date, day, dow }) => (
                  <th key={date}
                    onClick={() => !loading && bulkWorkOneDay(date)}
                    title={`${date} 전체 직원 근무 처리`}
                    style={{
                      padding:'6px 4px', fontSize:11, fontWeight:600, textAlign:'center',
                      borderBottom:'1px solid #e5e3de', borderRight:'0.5px solid #f0eeea',
                      minWidth:36,
                      color: dow===0?'#b83030':dow===6?'#1e4a8c':'#6b6860',
                      background: dow===0||dow===6 ? '#f0eeea' : '#f9f8f6',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      userSelect:'none'
                    }}>
                    <div>{day}</div>
                    <div style={{ fontSize:9, fontWeight:400 }}>{'일월화수목금토'[dow]}</div>
                    <div style={{ fontSize:9, color:'#2d5a3d', fontWeight:700, marginTop:1 }}>✓전체</div>
                  </th>
                ))}
                {/* 통계 헤더 */}
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#6b6860', borderBottom:'1px solid #e5e3de', borderLeft:'2px solid #e5e3de', textAlign:'center', minWidth:50, background:'#f9f8f6' }}>근무</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#c0601a', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:50, background:'#f9f8f6' }}>잔업h</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#c0601a', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:50, background:'#f9f8f6' }}>특근</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#2d5a3d', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:50, background:'#f9f8f6' }}>연차</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#1565c0', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:52, background:'#f9f8f6' }}>시간차h</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#b83030', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:50, background:'#f9f8f6' }}>결근</th>
                <th style={{ padding:'10px 8px', fontSize:11, fontWeight:600, color:'#2d5a3d', borderBottom:'1px solid #e5e3de', textAlign:'center', minWidth:56, background:'#f9f8f6' }}>잔여연차</th>
              </tr>
            </thead>
            <tbody>
              {workers.map(w => {
                const stats = getWorkerStats(w.id)
                const leaveQuota = w.leave_quota ?? 15
                const leaveUsed = w.leave_used ?? 0
                const leaveRemain = leaveQuota - leaveUsed
                const remainColor = leaveRemain <= 3 ? '#b83030' : leaveRemain <= 7 ? '#c0601a' : '#2d5a3d'
                return (
                  <tr key={w.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                    {/* 직원명 */}
                    <td style={{ padding:'8px 14px', borderRight:'1px solid #e5e3de', position:'sticky', left:0, background:'#fff', zIndex:5 }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{w.name}</div>
                      <div style={{ fontSize:10, color:'#a09e99' }}>{w.nationality}</div>
                      <div style={{ fontSize:10, color:'#6b6860', marginTop:2 }}>
                        잔여 <span style={{ fontWeight:700, color: remainColor }}>{leaveRemain}</span>일
                        <span style={{ color:'#a09e99' }}> / {leaveQuota}일</span>
                      </div>
                      <div style={{ display:'flex', gap:6, marginTop:4 }}>
                        <button type="button" onClick={() => bulkWorkOne(w.id)} disabled={loading}
                          style={{ fontSize:10, color:'#2d5a3d', border:'none', background:'#e8f2eb', borderRadius:4, cursor: loading ? 'not-allowed' : 'pointer', padding:'2px 6px', opacity: loading ? 0.7 : 1 }}>평일근무</button>
                        <button type="button" onClick={() => deleteWorker(w.id, w.name)}
                          style={{ fontSize:10, color:'#b83030', border:'none', background:'transparent', cursor:'pointer', padding:0 }}>삭제</button>
                      </div>
                    </td>
                    {/* 날짜 셀 */}
                    {days.map(({ date, dow }) => {
                      const rec = getRecord(w.id, date)
                      const p = rec ? parseForeignMemo(rec) : null
                      const plainWork = rec && rec.worked !== false && p && !p.absent && !p.leave && !rec.special_work && !(rec.overtime > 0) && !((p.hourly_leave || 0) > 0)
                      return (
                        <td key={date} onClick={() => openCellModal(w, date)}
                          style={{
                            padding:'4px 2px', textAlign:'center', cursor:'pointer',
                            borderRight:'0.5px solid #f0eeea', minWidth:36, height:44,
                            background: cellBg(rec, dow),
                            verticalAlign:'middle'
                          }}>
                          {p?.absent && <div style={{ fontSize:12 }}>❌</div>}
                          {p?.leave && <div style={{ fontSize:12 }}>🌿</div>}
                          {(p?.hourly_leave || 0) > 0 && <div style={{ fontSize:10, color:'#1565c0', fontWeight:600 }}>🕐{p.hourly_leave}h</div>}
                          {rec?.special_work && <div style={{ fontSize:11 }}>⭐</div>}
                          {rec?.overtime > 0 && <div style={{ fontSize:10, color:'#1e4a8c', fontWeight:600 }}>+{rec.overtime}h</div>}
                          {plainWork && <div style={{ fontSize:11, color:'#2d5a3d', fontWeight:700 }}>✓</div>}
                        </td>
                      )
                    })}
                    {/* 통계 */}
                    <td style={{ padding:'8px', textAlign:'center', borderLeft:'2px solid #e5e3de', fontWeight:700, color:'#2d5a3d', fontSize:13 }}>{stats.workedDays}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontWeight:700, color:'#1e4a8c', fontSize:13 }}>{stats.overtime}h</td>
                    <td style={{ padding:'8px', textAlign:'center', fontWeight:700, color:'#c0601a', fontSize:13 }}>{stats.special}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontWeight:700, color:'#2d5a3d', fontSize:13 }}>{stats.leaveDays}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontWeight:700, color:'#1565c0', fontSize:13 }}>{stats.hourlyLeaveHours}h</td>
                    <td style={{ padding:'8px', textAlign:'center', fontWeight:700, color:'#b83030', fontSize:13 }}>{stats.absentDays}</td>
                    <td style={{ padding:'8px', textAlign:'center', fontSize:13 }}>
                      <div style={{ fontWeight:700, color: remainColor }}>{leaveRemain}일</div>
                      <div style={{ fontSize:10, color:'#a09e99', fontWeight:400 }}>총 {leaveQuota} · 사용 {leaveUsed}</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 셀 입력 모달 */}
      {showCellModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setShowCellModal(null)}>
          <div style={{ background:'#fff', borderRadius:14, width:360, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700 }}>{showCellModal.worker.name}</div>
                <div style={{ fontSize:12, color:'#a09e99' }}>{showCellModal.date}</div>
              </div>
              <button type="button" onClick={() => setShowCellModal(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              {/* 근무 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={cellForm.worked && !cellForm.leave && !cellForm.absent}
                    onChange={e => {
                      const on = e.target.checked
                      setCellForm(f => ({
                        ...f,
                        worked: on,
                        leave: on ? false : f.leave,
                        absent: on ? false : f.absent
                      }))
                    }}
                    style={{ width:16, height:16 }} />
                  <span style={{ fontSize:13, fontWeight:600 }}>✓ 근무</span>
                </label>
              </div>
              {/* 결근 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={cellForm.absent}
                    onChange={e => {
                      const on = e.target.checked
                      setCellForm(f => ({
                        ...f,
                        absent: on,
                        leave: on ? false : f.leave,
                        worked: on ? false : f.worked,
                        hourlyLeave: on ? 0 : f.hourlyLeave
                      }))
                    }}
                    style={{ width:16, height:16 }} />
                  <span style={{ fontSize:13, fontWeight:600 }}>❌ 결근</span>
                </label>
              </div>
              {/* 연차 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={cellForm.leave}
                    onChange={e => {
                      const on = e.target.checked
                      setCellForm(f => ({
                        ...f,
                        leave: on,
                        absent: on ? false : f.absent,
                        worked: on ? false : f.worked,
                        hourlyLeave: on ? 0 : f.hourlyLeave
                      }))
                    }}
                    style={{ width:16, height:16 }} />
                  <span style={{ fontSize:13, fontWeight:600 }}>🌿 연차 (종일)</span>
                </label>
              </div>
              {/* 시간차 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:13, fontWeight:600, marginBottom:6 }}>🕐 시간차 (h)</label>
                <div style={{ fontSize:11, color:'#a09e99', marginBottom:6 }}>시간 단위 휴가 (연차·결근과 함께 사용 불가)</div>
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  disabled={cellForm.leave || cellForm.absent}
                  value={cellForm.hourlyLeave}
                  onChange={e => {
                    const v = e.target.value
                    const n = v === '' ? 0 : Number(v)
                    setCellForm(f => ({
                      ...f,
                      hourlyLeave: Number.isFinite(n) ? n : 0,
                      ...(Number.isFinite(n) && n > 0 ? { leave: false, absent: false, worked: true } : {})
                    }))
                  }}
                  style={{
                    width:'100%',
                    padding:'9px 12px',
                    border:'1px solid #e5e3de',
                    borderRadius:8,
                    fontSize:13,
                    fontFamily:'inherit',
                    boxSizing:'border-box',
                    opacity: cellForm.leave || cellForm.absent ? 0.5 : 1
                  }}
                />
              </div>
              {/* 잔업 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:13, fontWeight:600, marginBottom:6 }}>⏰ 잔업 시간 (h)</label>
                <input type="number" min={0} max={24} step={0.5} value={cellForm.overtime}
                  onChange={e => setCellForm(f => ({ ...f, overtime: e.target.value, worked: true }))}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
              </div>
              {/* 특근 */}
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={cellForm.special_work}
                    onChange={e => setCellForm(f => ({ ...f, special_work: e.target.checked, worked: e.target.checked ? true : f.worked }))}
                    style={{ width:16, height:16 }} />
                  <span style={{ fontSize:13, fontWeight:600 }}>⭐ 특근</span>
                </label>
              </div>
              {/* 메모 */}
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>메모</label>
                <input value={cellForm.memo} onChange={e => setCellForm({...cellForm, memo:e.target.value})}
                  placeholder="특이사항"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button type="button" onClick={() => setShowCellModal(null)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button type="button" onClick={saveCellForm} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 직원 등록 모달 */}
      {showWorkerModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setShowWorkerModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:400, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>외국인 직원 등록</span>
              <button type="button" onClick={() => setShowWorkerModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>이름 *</label>
                  <input value={workerForm.name} onChange={e => setWorkerForm({...workerForm, name:e.target.value})}
                    placeholder="이름"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>국적</label>
                  <input value={workerForm.nationality} onChange={e => setWorkerForm({...workerForm, nationality:e.target.value})}
                    placeholder="예) 베트남"
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>메모</label>
                <textarea value={workerForm.memo} onChange={e => setWorkerForm({...workerForm, memo:e.target.value})}
                  placeholder="비고" rows={2}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button type="button" onClick={() => setShowWorkerModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button type="button" onClick={addWorker} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '등록 중...' : '등록하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 연차 부여 모달 */}
      {showQuotaModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setShowQuotaModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:500, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>외국인 직원 연차 부여</span>
              <button type="button" onClick={() => setShowQuotaModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['직원','국적','총 연차','사용일수','잔여'].map(h => (
                      <th key={h} style={{ padding:'8px 12px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:['총 연차','사용일수','잔여'].includes(h)?'center':'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workers.map(w => <ForeignQuotaRow key={w.id} worker={w} onSave={saveQuota} />)}
                </tbody>
              </table>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', justifyContent:'flex-end' }}>
              <button type="button" onClick={() => setShowQuotaModal(false)}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ForeignQuotaRow({ worker, onSave }) {
  const [quota, setQuota] = useState(worker.leave_quota || 15)
  const [used, setUsed] = useState(worker.leave_used || 0)
  const remain = quota - used
  return (
    <tr style={{ borderBottom:'1px solid #e5e3de' }}>
      <td style={{ padding:'10px 12px', fontWeight:600 }}>{worker.name}</td>
      <td style={{ padding:'10px 12px', fontSize:12, color:'#a09e99' }}>{worker.nationality}</td>
      <td style={{ padding:'8px 12px', textAlign:'center' }}>
        <input type="number" value={quota} onChange={e => setQuota(e.target.value)} min={0}
          style={{ width:60, padding:'6px 8px', border:'1px solid #e5e3de', borderRadius:6, fontSize:13, textAlign:'center', fontFamily:'inherit' }} />
      </td>
      <td style={{ padding:'8px 12px', textAlign:'center' }}>
        <input type="number" value={used} onChange={e => setUsed(e.target.value)} min={0}
          style={{ width:60, padding:'6px 8px', border:'1px solid #e5e3de', borderRadius:6, fontSize:13, textAlign:'center', fontFamily:'inherit' }} />
      </td>
      <td style={{ padding:'10px 12px', textAlign:'center' }}>
        <span style={{ fontWeight:700, color: remain<=3?'#b83030':remain<=7?'#c0601a':'#2d5a3d', marginRight:8 }}>{remain}일</span>
        <button type="button" onClick={() => onSave(worker.id, quota, used)}
          style={{ padding:'3px 10px', background:'#e8f2eb', color:'#2d5a3d', border:'none', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600 }}>저장</button>
      </td>
    </tr>
  )
}
